import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-navy flex items-center justify-center px-5 py-10 relative overflow-hidden"
    >
      {/* backdrop hero image */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'url("https://images.unsplash.com/photo-1708778002586-37c4f23b7f3f?crop=entropy&cs=srgb&fm=jpg&q=85")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/90" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md border border-white/10 bg-navy/80 backdrop-blur-xl p-8 md:p-10"
      >
        <Link to="/" className="chapter-num mb-8 inline-block">
          ← Back to site
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-sm border border-gold/40 bg-gold/10 flex items-center justify-center">
            <Lock size={16} className="text-gold" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-white">B'Premium</h1>
            <p className="chapter-num">Admin login</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <Label htmlFor="email" className="text-xs tracking-widest uppercase text-white/70">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              className="mt-2 bg-white/5 border-white/10 text-white"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs tracking-widest uppercase text-white/70">
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-2 bg-white/5 border-white/10 text-white"
            />
          </div>

          {error && (
            <p
              data-testid="login-error"
              className="text-sm text-red-400 border border-red-400/30 bg-red-500/5 px-3 py-2 rounded-sm"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full py-3 rounded-full bg-gold text-navy text-xs tracking-[0.25em] uppercase font-bold hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
